import React, { useEffect, useState } from 'react';
import { DollarSign, RefreshCw, Plus } from 'lucide-react';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge } from '../../components/UI';
import Breadcrumb from '../../components/Breadcrumb';
import { partnerService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import toast from 'react-hot-toast';

const PARTNER_NAMES = {
  partner_halim:     'Abdul Halim Adib Karimi',
  partner_nuroollah: 'Nuroollah Mansoor',
};
const TYPE_COLOR = { contribution:'green', expense:'orange', settlement:'blue', repayment:'red' };

export default function PartnersPage() {
  const [summary,   setSummary]   = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [activeTab, setActiveTab] = useState('partner_halim');
  const [repayOpen, setRepayOpen] = useState(false);
  const [repayForm, setRepayForm] = useState({ partner:'', amount:'', description:'' });
  const [saving,    setSaving]    = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    try { const r = await partnerService.getSummary(); setSummary(r.data.data); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSummary(); }, []);

  const handleRepay = async () => {
    if (!repayForm.partner || !repayForm.amount || Number(repayForm.amount) <= 0) return toast.error('Partner and amount required');
    setSaving(true);
    try {
      await partnerService.repay(repayForm);
      toast.success('Repayment recorded');
      setRepayOpen(false); setRepayForm({ partner:'', amount:'', description:'' }); fetchSummary();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const partnerData = summary?.[activeTab];

  return (
    <div>
      <Breadcrumb/>
      <PageHeader title="Partner Management" subtitle="Track contributions, expenses and settlements"
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={fetchSummary} disabled={loading}><RefreshCw size={15} className={loading?'animate-spin':''}/></Button>
          <Button onClick={()=>{ setRepayForm({ partner:activeTab, amount:'', description:'' }); setRepayOpen(true); }} className="bg-green-600 hover:bg-green-700 text-white"><Plus size={15}/> Repay Partner</Button>
        </div>}
      />

      {/* Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {Object.entries(PARTNER_NAMES).map(([key, name]) => {
          const p = summary?.[key];
          return (
            <div key={key} onClick={()=>setActiveTab(key)}
              className={`rounded-xl border-2 p-5 cursor-pointer transition-all ${activeTab===key?'border-blue-500 bg-blue-50':'border-gray-200 bg-white hover:border-blue-200'}`}>
              <div className="flex justify-between items-start mb-3">
                <div><p className="text-xs text-gray-400 mb-1">Partner</p><p className="font-bold text-gray-800">{name}</p></div>
                <p className={`text-2xl font-bold ${(p?.balance||0)>0?'text-orange-600':'text-green-600'}`}>{formatCurrency(p?.balance||0)}</p>
              </div>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="bg-green-50 rounded-lg p-2 text-center"><p className="text-gray-400">Contributed</p><p className="font-bold text-green-700 mt-0.5">{formatCurrency(p?.totalContributed||0)}</p></div>
                <div className="bg-blue-50 rounded-lg p-2 text-center"><p className="text-gray-400">Repaid</p><p className="font-bold text-blue-700 mt-0.5">{formatCurrency(p?.totalRepaid||0)}</p></div>
                <div className="bg-orange-50 rounded-lg p-2 text-center"><p className="text-gray-400">Balance</p><p className={`font-bold mt-0.5 ${(p?.balance||0)>0?'text-orange-600':'text-gray-500'}`}>{formatCurrency(p?.balance||0)}</p></div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Transactions */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex justify-between">
          <p className="text-sm font-semibold text-gray-700">{PARTNER_NAMES[activeTab]} — Transactions</p>
          <span className="text-xs text-gray-400">{partnerData?.transactions?.length||0} records</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b border-gray-100"><tr>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Type</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Description</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Amount</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {loading ? <tr><td colSpan={4} className="text-center py-10 text-gray-400">Loading...</td></tr>
            : !partnerData?.transactions?.length ? <tr><td colSpan={4} className="text-center py-10 text-gray-400">No transactions yet</td></tr>
            : partnerData.transactions.map(tx=>(
              <tr key={tx._id} className="hover:bg-gray-50">
                <td className="px-4 py-3 text-xs text-gray-400">{toJalali(tx.date)}</td>
                <td className="px-4 py-3"><Badge color={TYPE_COLOR[tx.type]||'gray'}>{tx.type}</Badge></td>
                <td className="px-4 py-3 text-gray-600">{tx.description||'-'}</td>
                <td className={`px-4 py-3 text-right font-semibold ${tx.type==='repayment'?'text-red-500':'text-green-700'}`}>
                  {tx.type==='repayment'?'- ':''}{formatCurrency(tx.amount)}
                </td>
              </tr>
            ))}
          </tbody>
          {(partnerData?.transactions?.length||0)>0 && (
            <tfoot className="bg-gray-50 border-t-2 border-gray-200">
              <tr>
                <td colSpan={3} className="px-4 py-3 text-sm font-semibold text-gray-600">Balance Owed</td>
                <td className={`px-4 py-3 text-right text-base font-bold ${(partnerData?.balance||0)>0?'text-orange-600':'text-green-700'}`}>{formatCurrency(partnerData?.balance||0)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Repay Modal */}
      <Modal isOpen={repayOpen} onClose={()=>setRepayOpen(false)} title="Repay Partner from Pharmacy Cash" size="sm">
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">Deducts from Pharmacy Cash and records in accounting.</div>
          <FormField label="Partner" required>
            <Select value={repayForm.partner} onChange={e=>setRepayForm(p=>({...p,partner:e.target.value}))}>
              <option value="">Select partner</option>
              {Object.entries(PARTNER_NAMES).map(([k,v])=><option key={k} value={k}>{v}</option>)}
            </Select>
          </FormField>
          <FormField label="Amount" required>
            <Input type="number" step="0.01" min="0" value={repayForm.amount} onChange={e=>setRepayForm(p=>({...p,amount:e.target.value}))} placeholder="0.00"/>
          </FormField>
          {repayForm.partner && summary?.[repayForm.partner] && (
            <div className="bg-orange-50 rounded-lg p-3 text-xs">
              <p className="text-orange-700">Balance owed: <strong className="text-xl text-orange-600">{formatCurrency(summary[repayForm.partner].balance)}</strong></p>
            </div>
          )}
          <FormField label="Description"><Input value={repayForm.description} onChange={e=>setRepayForm(p=>({...p,description:e.target.value}))} placeholder="Reason"/></FormField>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={()=>setRepayOpen(false)}>Cancel</Button>
            <Button onClick={handleRepay} loading={saving} className="bg-green-600 hover:bg-green-700 text-white"><DollarSign size={14}/> Record Repayment</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
